// Konva renderer — the only canvas layer. Reconciles store -> stage by id so
// interactions (drag/transform) aren't destroyed mid-gesture. Owns the view
// transform, the selection Transformer, and a draw-preview node.

import Konva from 'konva';
import { coordsToPathData, lobedPoints, coniferNeedles } from './geometry';
import { visibleAtMonth } from '../dates';

const DEG = 180 / Math.PI;
const RAD = Math.PI / 180;

function variantKey(el) {
  if (el.type === 'ellipse') return `ellipse:${el.data?.canopyStyle || 'smooth'}`;
  return el.type;
}

// Conifer: filled circle behind radiating needle lines (matches v1).
function coniferSceneFunc(ctx, shape) {
  const r = shape.getAttr('radius') || 0;
  const needles = shape.getAttr('needles') || [];
  const fill = shape.fill();
  if (fill) {
    ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.closePath();
    ctx.fillStyle = fill; ctx.fill();
  }
  ctx.beginPath();
  for (const n of needles) { ctx.moveTo(n.ix, n.iy); ctx.lineTo(n.ox, n.oy); }
  ctx.lineCap = 'round';
  ctx.strokeStyle = shape.stroke() || '#000';
  ctx.lineWidth = shape.strokeWidth() || 1;
  ctx.stroke();
}
function coniferHitFunc(ctx, shape) {
  const r = shape.getAttr('radius') || 0;
  ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.closePath();
  ctx.fillStrokeShape(shape);
}

export default class Renderer {
  constructor(container, store) {
    this.container = container;
    this.store = store;
    this.imageCache = new Map();
    this.nodeMap = new Map(); // element id -> Konva node
    this.draggable = false;   // toggled by the active tool

    this.stage = new Konva.Stage({
      container,
      width: container.clientWidth || 1,
      height: container.clientHeight || 1,
    });

    this.gridLayer = new Konva.Layer({ listening: false });
    this.contentLayer = new Konva.Layer();
    this.stage.add(this.gridLayer, this.contentLayer);

    this.viewGroup = new Konva.Group();
    this.contentLayer.add(this.viewGroup);
    this.labelsGroup = new Konva.Group({ listening: true });
    this.viewGroup.add(this.labelsGroup);
    this.labelMap = new Map(); // element id -> { node, el }
    this.hoverGroup = new Konva.Group({ listening: false });
    this.viewGroup.add(this.hoverGroup);
    this.hoverId = null;
    this.vertexGroup = new Konva.Group({ name: 'vertices' }); // poly/line edit handles
    this.viewGroup.add(this.vertexGroup);

    this.transformer = new Konva.Transformer({
      rotateEnabled: true,
      ignoreStroke: true,
      borderStroke: '#087332',
      anchorStroke: '#087332',
      anchorFill: '#ffffff',
      anchorSize: 8,
      anchorCornerRadius: 4,
    });
    this.contentLayer.add(this.transformer);

    this.previewNode = null;

    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(this._onResize);
      this._ro.observe(container);
    }

    this._unsub = store.subscribe((state, meta) => this.reconcile(state, meta));
  }

  resize() {
    this.stage.size({ width: this.container.clientWidth || 1, height: this.container.clientHeight || 1 });
    this.reconcile(this.store.state, {});
  }

  stageSize() { return { width: this.stage.width(), height: this.stage.height() }; }

  applyView(view) {
    this.viewGroup.position({ x: view.posX || 0, y: view.posY || 0 });
    this.viewGroup.scale({ x: view.scale || 1, y: view.scale || 1 });
  }

  // Pointer position in plan coordinates (inside the scaled/positioned group).
  pointerPlanPos() {
    return this.viewGroup.getRelativePointerPosition();
  }

  setDraggable(on) {
    this.draggable = on;
    for (const [id, node] of this.nodeMap) {
      const el = this.store.state.elements.find((element) => element.id === id);
      node.draggable(on && !el?.data?.locked);
    }
    for (const { node, el } of this.labelMap.values()) node.draggable(on && !el.data?.locked);
  }

  reconcile(state, meta = {}) {
    if (!state.loaded) return;

    let view = state.view;
    if (!view || !view.scale) view = this.fitView(state);
    this.applyView(view);
    this.renderGrid(state, view);

    const layerMap = new Map(state.layers.map((l) => [l.id, l]));
    const seen = new Set();

    for (const el of state.elements) {
      const layer = el.data?.layer ? layerMap.get(el.data.layer) : null;
      const hiddenByLayer = layer && layer.visible === false;
      const hiddenByTime = state.timelineOn && !visibleAtMonth(el, state.timelineMonth);
      const hiddenImage = el.type === 'image' && el.data?.visible === false;
      if (hiddenByLayer || hiddenByTime || hiddenImage) {
        const existing = this.nodeMap.get(el.id);
        if (existing) { existing.destroy(); this.nodeMap.delete(el.id); }
        continue;
      }
      seen.add(el.id);
      let node = this.nodeMap.get(el.id);
      if (!node || node.getAttr('variant') !== variantKey(el)) {
        if (node) node.destroy();
        node = this.buildNode(el);
        this.nodeMap.set(el.id, node);
        this.viewGroup.add(node);
      } else {
        this.applyNode(node, el);
      }
      const locked = !!el.data?.locked;
      node.draggable(this.draggable && !locked);
      node.listening(!locked);
      node.opacity((layer ? (layer.opacity ?? 1) : 1) * (el.type === 'image' ? (el.data.opacity ?? 255) / 255 : 1));
    }

    // Remove nodes whose elements are gone
    for (const [id, node] of this.nodeMap) {
      if (!seen.has(id)) { node.destroy(); this.nodeMap.delete(id); }
    }

    // Stack nodes in element-array order (z-order), then overlays on top.
    for (const el of state.elements) this.nodeMap.get(el.id)?.moveToTop();
    this.labelsGroup.moveToTop();
    this.hoverGroup.moveToTop();

    this.renderLabels(state, layerMap);
    this.syncTransformer(state);
    this.renderVertices(state);
    this.contentLayer.batchDraw();
  }

  // Vertex + control-point handles for the single selected polygon/line, so its
  // points and curve/bezier controls can be dragged. Sizes are divided by the
  // view scale to stay ~constant on screen. Handles carry the coord index + slot
  // they edit (anchor / quadratic cp / cubic cp1,cp2).
  renderVertices(state) {
    this.vertexGroup.destroyChildren();
    this.vertexGroup.position({ x: 0, y: 0 }); // reset any drag-follow offset
    if (!this.draggable) return;
    const sel = state.selection || [];
    if (sel.length !== 1) return;
    const el = state.elements.find((e) => e.id === sel[0]);
    if (!el || el.data?.locked || (el.type !== 'polygon' && el.type !== 'line')) return;
    const coords = el.data?.coordinates || [];
    if (!coords.some((c) => Array.isArray(c) && typeof c[0] === 'number')) return;
    const scale = state.view?.scale || 1;
    const aR = 5 / scale; const cR = 4 / scale; const sw = 1.5 / scale;
    const start = coords.find((c) => Array.isArray(c) && typeof c[0] === 'number');
    // Faint connector from a control point to the anchor(s) it pulls — drawn
    // first so the handles sit on top.
    const link = (ax, ay, bx, by) => this.vertexGroup.add(new Konva.Line({
      points: [ax, ay, bx, by], stroke: '#2f8f57', strokeWidth: 1, strokeScaleEnabled: false,
      dash: [4 / scale, 3 / scale], listening: false, opacity: 0.7,
    }));
    const anchor = (vidx, x, y) => this.vertexGroup.add(new Konva.Circle({
      name: 'vhandle', elId: el.id, vidx, vkind: 'anchor',
      x, y, radius: aR, fill: '#ffffff', stroke: '#087332', strokeWidth: sw, strokeScaleEnabled: false,
      shadowColor: '#0b1f12', shadowBlur: 4 / scale, shadowOpacity: 0.28, shadowOffsetY: 0.5 / scale,
      draggable: true,
    }));
    const cp = (vidx, slot, x, y) => this.vertexGroup.add(new Konva.Circle({
      name: 'vhandle', elId: el.id, vidx, vkind: 'cp', slot,
      x, y, radius: cR, fill: '#0CB04D', stroke: '#ffffff', strokeWidth: sw, strokeScaleEnabled: false,
      shadowColor: '#0b1f12', shadowBlur: 3 / scale, shadowOpacity: 0.25,
      draggable: true,
    }));
    let prev = start; // previous anchor point, for connector geometry
    coords.forEach((c, i) => {
      if (!Array.isArray(c)) return;
      if (c[0] === 'closing') {
        if (c[1] === 'curve') { link(c[2], c[3], prev[0], prev[1]); link(c[2], c[3], start[0], start[1]); cp(i, 'q', c[2], c[3]); }
        else if (c[1] === 'bezier') { link(c[2], c[3], prev[0], prev[1]); link(c[4], c[5], start[0], start[1]); cp(i, 'c1', c[2], c[3]); cp(i, 'c2', c[4], c[5]); }
        return;
      }
      if (c[2] === 'curve') { link(c[3], c[4], prev[0], prev[1]); link(c[3], c[4], c[0], c[1]); cp(i, 'q', c[3], c[4]); }
      else if (c[2] === 'bezier') { link(c[3], c[4], prev[0], prev[1]); link(c[5], c[6], c[0], c[1]); cp(i, 'c1', c[3], c[4]); cp(i, 'c2', c[5], c[6]); }
      anchor(i, c[0], c[1]);
      prev = c;
    });
    this.vertexGroup.moveToTop();
  }

  // Live-write a dragged handle's position back into the element coordinates and
  // refresh the path (called on every dragmove; commit happens on dragend).
  applyHandle(node) {
    const el = this.store.state.elements.find((e) => e.id === node.getAttr('elId'));
    if (!el || el.data?.locked) return;
    const coords = el.data.coordinates;
    const c = coords[node.getAttr('vidx')];
    if (!Array.isArray(c)) return;
    const x = node.x(); const y = node.y();
    const closing = c[0] === 'closing';
    if (node.getAttr('vkind') === 'anchor') { c[0] = x; c[1] = y; }
    else {
      const slot = node.getAttr('slot');
      if (slot === 'q' || slot === 'c1') { if (closing) { c[2] = x; c[3] = y; } else { c[3] = x; c[4] = y; } }
      else if (slot === 'c2') { if (closing) { c[4] = x; c[5] = y; } else { c[5] = x; c[6] = y; } }
    }
    const shape = this.nodeMap.get(el.id);
    if (shape) { shape.data(coordsToPathData(coords, el.type === 'polygon')); this.contentLayer.batchDraw(); }
  }

  // Remove a numeric anchor (keeps at least two). Returns true if it changed.
  removeVertex(elId, vidx) {
    const el = this.store.state.elements.find((e) => e.id === elId);
    if (!el || el.data?.locked) return false;
    const coords = el.data.coordinates;
    if (!Array.isArray(coords[vidx]) || typeof coords[vidx][0] !== 'number') return false;
    const numeric = coords.filter((c) => Array.isArray(c) && typeof c[0] === 'number');
    if (numeric.length <= 2) return false;
    coords.splice(vidx, 1);
    return true;
  }

  // Insert a straight vertex at (px,py) on the nearest segment. Returns true if
  // it changed.
  addVertexNear(elId, px, py) {
    const el = this.store.state.elements.find((e) => e.id === elId);
    if (!el || el.data?.locked) return false;
    const coords = el.data.coordinates;
    const anchors = [];
    coords.forEach((c, idx) => { if (Array.isArray(c) && typeof c[0] === 'number') anchors.push({ idx, x: c[0], y: c[1] }); });
    if (anchors.length < 2) return false;
    let best = { d: Infinity, after: anchors[0].idx };
    const consider = (a, b) => {
      const vx = b.x - a.x; const vy = b.y - a.y;
      const len2 = vx * vx + vy * vy || 1;
      let t = ((px - a.x) * vx + (py - a.y) * vy) / len2;
      t = Math.max(0, Math.min(1, t));
      const d = Math.hypot(px - (a.x + vx * t), py - (a.y + vy * t));
      if (d < best.d) best = { d, after: a.idx };
    };
    for (let k = 0; k < anchors.length - 1; k += 1) consider(anchors[k], anchors[k + 1]);
    if (el.type === 'polygon') consider(anchors[anchors.length - 1], anchors[0]);
    coords.splice(best.after + 1, 0, [px, py]);
    return true;
  }

  // Species + custom labels as text near each element. Cheap to rebuild.
  renderLabels(state, layerMap) {
    this.labelsGroup.destroyChildren();
    this.labelsGroup.moveToTop();
    this.labelMap.clear();
    if (state.settings.showLabels === false) return;
    // Konva sizes every text node through a shared 2D canvas context. A headless
    // browser whose context has no measureText makes the Konva.Text constructor
    // throw, so skip the labels rather than take the whole render — and the plan
    // load that runs it — down with them.
    if (!canMeasureText()) return;
    for (const el of state.elements) {
      const d = el.data || {};
      const layer = d.layer ? layerMap.get(d.layer) : null;
      if (layer && layer.visible === false) continue;
      if (state.timelineOn && !visibleAtMonth(el, state.timelineMonth)) continue;
      const text = labelText(d.labels, state.species);
      if (!text) continue;
      const c = centerOf(el);
      if (!c) continue;
      const t = new Konva.Text({
        text, x: c.x + (d.labelX || 0), y: c.y + (d.labelY || 0),
        fontSize: 14, fontFamily: 'sans-serif', fill: '#1f2329', align: 'center',
        name: 'label', elId: el.id, listening: !d.locked, draggable: this.draggable && !d.locked,
      });
      t.offsetX(t.width() / 2); t.offsetY(t.height() / 2);
      this.labelsGroup.add(t);
      this.labelMap.set(el.id, { node: t, el });
    }
  }

  // Reposition labels to follow their element's live node position (during a
  // drag, before the move is committed). Cheap — only labelled elements.
  repositionLabels() {
    for (const { node, el } of this.labelMap.values()) {
      const shape = this.nodeMap.get(el.id);
      if (!shape) continue;
      const d = el.data || {};
      let cx; let cy;
      if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') {
        cx = shape.x(); cy = shape.y();
      } else {
        const c = centerOf(el);
        if (!c) continue;
        cx = c.x + shape.x(); cy = c.y + shape.y();
      }
      node.position({ x: cx + (d.labelX || 0), y: cy + (d.labelY || 0) });
    }
    // Hover ring follows the dragged/transformed shape too.
    const hover = this.hoverGroup.getChildren()[0];
    const shape = this.hoverId && this.nodeMap.get(this.hoverId);
    if (hover && shape) hover.position(shape.position());
    this.contentLayer.batchDraw();
  }

  // Minor/major grid lines in screen space (so they stay crisp), spaced by the
  // current unit dimensions and view scale — scales/pans with the plan, like v1.
  renderGrid(state, view) {
    this.gridLayer.destroyChildren();
    if (state.settings?.showGrid === false) { this.gridLayer.batchDraw(); return; }
    const scale = view.scale || 1;
    const w = this.stage.width(); const h = this.stage.height();
    const metric = state.settings?.units !== 'imperial';
    const dims = metric ? [1, 10, 100, 1000, 10000, 100000] : [2.54, 30.48, 304.8, 3048, 30480];
    const visible = dims.map((a) => ({ a, px: a * scale })).filter((d) => d.px >= 8);
    const minor = visible[0]; const major = visible[1];
    const draw = (spacing, stroke, strokeWidth) => {
      if (!spacing || spacing < 4) return;
      const sx = (((view.posX || 0) % spacing) + spacing) % spacing;
      for (let x = sx; x <= w; x += spacing) this.gridLayer.add(new Konva.Line({ points: [Math.round(x) + 0.5, 0, Math.round(x) + 0.5, h], stroke, strokeWidth, listening: false }));
      const sy = (((view.posY || 0) % spacing) + spacing) % spacing;
      for (let y = sy; y <= h; y += spacing) this.gridLayer.add(new Konva.Line({ points: [0, Math.round(y) + 0.5, w, Math.round(y) + 0.5], stroke, strokeWidth, listening: false }));
    };
    if (minor) draw(minor.px, 'rgba(20,28,40,0.05)', 1);
    if (major) draw(major.px, 'rgba(20,28,40,0.11)', 1);
    // origin axes
    this.gridLayer.add(new Konva.Line({ points: [Math.round(view.posX) + 0.5, 0, Math.round(view.posX) + 0.5, h], stroke: 'rgba(20,28,40,0.2)', strokeWidth: 1, listening: false }));
    this.gridLayer.add(new Konva.Line({ points: [0, Math.round(view.posY) + 0.5, w, Math.round(view.posY) + 0.5], stroke: 'rgba(20,28,40,0.2)', strokeWidth: 1, listening: false }));
    this.gridLayer.batchDraw();
  }

  // Yellow hover outline matching the element's exact shape (a plain circle for
  // plants/canopies, the outline for rect/poly/line). Stroke stays ~3px at any
  // zoom (strokeScaleEnabled:false).
  setHover(el) {
    if ((el?.id || null) === this.hoverId) return;
    this.hoverId = el?.id || null;
    this.hoverGroup.destroyChildren();
    if (el) {
      const node = this.buildHoverNode(el);
      if (node) { this.hoverGroup.add(node); this.hoverGroup.moveToTop(); }
    }
    this.contentLayer.batchDraw();
  }

  buildHoverNode(el) {
    const d = el.data || {};
    const common = { stroke: '#f9bc21', strokeWidth: 3, strokeScaleEnabled: false, fillEnabled: false, listening: false };
    switch (el.type) {
      case 'ellipse':
        return new Konva.Ellipse({ ...common, x: d.x, y: d.y, radiusX: (d.width || 0) / 2, radiusY: (d.height || d.width || 0) / 2 });
      case 'rectangle':
        return new Konva.Rect({ ...common, x: d.x, y: d.y, width: d.width || 0, height: d.height || 0, offsetX: (d.width || 0) / 2, offsetY: (d.height || 0) / 2, rotation: (d.rotation || 0) * DEG });
      case 'polygon':
        return new Konva.Path({ ...common, data: coordsToPathData(d.coordinates, true) });
      case 'line':
        return new Konva.Path({ ...common, lineCap: 'round', lineJoin: 'round', data: coordsToPathData(d.coordinates, false) });
      case 'image':
        return new Konva.Rect({ ...common, x: d.x, y: d.y, width: d.width || 0, height: d.height || 0, offsetX: (d.width || 0) / 2, offsetY: (d.height || 0) / 2, rotation: (d.rotation || 0) * DEG });
      default:
        return null;
    }
  }

  buildNode(el) {
    const d = el.data || {};
    let node;
    const base = { id: el.id, elType: el.type, variant: variantKey(el), name: 'element' };
    switch (el.type) {
      case 'ellipse': {
        const r = (d.width || 0) / 2;
        if (d.canopyStyle === 'lobed') {
          node = new Konva.Line({ ...base, x: d.x, y: d.y, points: lobedPoints(d.width || 0, d.canopySeed || 1), closed: true, tension: 0.5, canopy: 'lobed', baseRadius: r });
        } else if (d.canopyStyle === 'conifer') {
          const cn = coniferNeedles(d.width || 0, d.canopySeed || 1);
          node = new Konva.Shape({ ...base, x: d.x, y: d.y, sceneFunc: coniferSceneFunc, hitFunc: coniferHitFunc, radius: cn.radius, needles: cn.needles, canopy: 'conifer', baseRadius: r });
        } else {
          node = new Konva.Ellipse({ ...base, x: d.x, y: d.y, radiusX: r, radiusY: (d.height || d.width || 0) / 2 });
        }
        break;
      }
      case 'rectangle':
        node = new Konva.Rect({ ...base, x: d.x, y: d.y, width: d.width || 0, height: d.height || 0, offsetX: (d.width || 0) / 2, offsetY: (d.height || 0) / 2, rotation: (d.rotation || 0) * DEG });
        break;
      case 'polygon':
        node = new Konva.Path({ ...base, data: coordsToPathData(d.coordinates, true) });
        node.hitStrokeWidth(Math.max(10, d.strokeWidth || 0));
        break;
      case 'line':
        node = new Konva.Path({ ...base, data: coordsToPathData(d.coordinates, false), lineCap: 'round', lineJoin: 'round' });
        node.hitStrokeWidth(Math.max(12, d.strokeWidth || 0));
        break;
      case 'image': {
        const image = this.loadImage(d.url);
        const width = d.width || image?.width || 0;
        const height = d.height || image?.height || 0;
        node = new Konva.Image({ ...base, image, url: d.url, x: d.x || 0, y: d.y || 0, width, height, offsetX: width / 2, offsetY: height / 2, rotation: (d.rotation || 0) * DEG });
        break;
      }
      default:
        return new Konva.Group(base);
    }
    this.styleNode(node, el);
    return node;
  }

  applyNode(node, el) {
    const d = el.data || {};
    if (el.type === 'ellipse') {
      const r = (d.width || 0) / 2;
      if (d.canopyStyle === 'lobed') {
        node.points(lobedPoints(d.width || 0, d.canopySeed || 1)); node.position({ x: d.x, y: d.y }); node.setAttr('baseRadius', r);
      } else if (d.canopyStyle === 'conifer') {
        const cn = coniferNeedles(d.width || 0, d.canopySeed || 1);
        node.setAttr('radius', cn.radius); node.setAttr('needles', cn.needles); node.position({ x: d.x, y: d.y }); node.setAttr('baseRadius', r);
      } else {
        node.position({ x: d.x, y: d.y }); node.radiusX(r); node.radiusY((d.height || d.width || 0) / 2);
      }
    } else if (el.type === 'rectangle' || el.type === 'image') {
      const image = el.type === 'image' ? this.loadImage(d.url) : null;
      const width = d.width || image?.width || 0;
      const height = d.height || image?.height || 0;
      node.position({ x: d.x, y: d.y }); node.width(width); node.height(height);
      node.offsetX(width / 2); node.offsetY(height / 2); node.rotation((d.rotation || 0) * DEG);
      if (el.type === 'image') { node.image(image); node.setAttr('url', d.url); }
    } else {
      node.data(coordsToPathData(d.coordinates, el.type === 'polygon')); node.position({ x: 0, y: 0 });
    }
    this.styleNode(node, el);
  }

  styleNode(node, el) {
    const d = el.data || {};
    if (el.type === 'image') return;
    node.fill(el.type === 'line' ? undefined : (d.fillColor || undefined));
    node.stroke(d.strokeColor || undefined);
    node.strokeWidth(d.strokeWidth != null ? d.strokeWidth : (d.strokeColor ? 1 : 0));
  }

  syncTransformer(state) {
    // Resize/rotate handles only for unlocked rect, ellipse, and image elements.
    const nodes = (state.selection || [])
      .map((id) => this.nodeMap.get(id))
      .filter((n) => n && !state.elements.find((el) => el.id === n.id())?.data?.locked && ['rectangle', 'ellipse', 'image'].includes(n.getAttr('elType')));
    this.transformer.keepRatio(nodes.length === 1 && nodes[0].getAttr('elType') === 'image');
    this.transformer.nodes(this.draggable ? nodes : []);
    this.transformer.moveToTop();
  }

  // Read a node's current geometry back into element data (after drag/transform).
  bakeNode(node, el) {
    if (el.data?.locked) return;
    const d = el.data;
    if (el.type === 'ellipse' && node.getAttr('canopy')) {
      const scale = (Math.abs(node.scaleX()) + Math.abs(node.scaleY())) / 2 || 1;
      const r = Math.max(2, node.getAttr('baseRadius') * scale);
      node.scale({ x: 1, y: 1 });
      d.x = node.x(); d.y = node.y(); d.width = r * 2; d.height = r * 2;
    } else if (el.type === 'ellipse') {
      const rx = Math.max(2, node.radiusX() * node.scaleX());
      const ry = Math.max(2, node.radiusY() * node.scaleY());
      node.scale({ x: 1, y: 1 }); node.radiusX(rx); node.radiusY(ry);
      d.x = node.x(); d.y = node.y(); d.width = rx * 2; d.height = ry * 2;
    } else if (el.type === 'rectangle' || el.type === 'image') {
      const w = Math.max(4, node.width() * node.scaleX());
      const h = Math.max(4, node.height() * node.scaleY());
      node.scale({ x: 1, y: 1 }); node.width(w); node.height(h); node.offsetX(w / 2); node.offsetY(h / 2);
      d.x = node.x(); d.y = node.y(); d.width = w; d.height = h; d.rotation = node.rotation() * RAD;
    } else {
      // poly/line: fold node offset into coordinates, reset node to origin
      const dx = node.x(); const dy = node.y();
      d.coordinates = shiftCoords(d.coordinates, dx, dy);
      node.position({ x: 0, y: 0 });
    }
  }

  // Read an image once and reconcile when it becomes available. URLs stay in
  // runtime state only. The content serializer removes them before saving.
  loadImage(url) {
    if (!url) return null;
    const cached = this.imageCache.get(url);
    if (cached) return cached;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      this.imageCache.set(url, image);
      this.reconcile(this.store.state, {});
    };
    image.src = url;
    this.imageCache.set(url, image);
    return image;
  }

  // During an element drag, slide the selected poly/line's vertex handles so they
  // track the shape (the handles hold absolute coords; the node holds the offset).
  offsetVertices(node) {
    const t = node?.getAttr?.('elType');
    if (t === 'polygon' || t === 'line') {
      this.vertexGroup.position({ x: node.x(), y: node.y() });
      this.contentLayer.batchDraw();
    }
  }

  // Abort any in-progress Konva drag (e.g. when a second finger starts a pinch).
  stopAllDrag() {
    for (const n of this.nodeMap.values()) if (n.isDragging?.()) n.stopDrag();
    for (const { node } of this.labelMap.values()) if (node.isDragging?.()) node.stopDrag();
  }

  // PNG blob of the current canvas (no grid / handles / hover) for the plan
  // overview thumbnail. Returns null if the canvas is tainted (cross-origin img).
  async snapshotBlob() {
    const gridVis = this.gridLayer.visible();
    const hoverVis = this.hoverGroup.visible();
    const tfNodes = this.transformer.nodes();
    this.gridLayer.visible(false); this.hoverGroup.visible(false);
    this.transformer.nodes([]);
    this.stage.draw();
    try {
      const max = Math.max(this.stage.width(), this.stage.height()) || 1;
      const pixelRatio = Math.min(1, 900 / max);
      return await this.stage.toBlob({ pixelRatio, mimeType: 'image/png' });
    } catch (_) {
      return null;
    } finally {
      this.gridLayer.visible(gridVis); this.hoverGroup.visible(hoverVis);
      this.transformer.nodes(tfNodes);
      this.stage.draw();
    }
  }

  // --- preview node for draw tools -----------------------------------------
  setPreview(node) { this.clearPreview(); this.previewNode = node; this.viewGroup.add(node); this.contentLayer.batchDraw(); }
  clearPreview() { if (this.previewNode) { this.previewNode.destroy(); this.previewNode = null; this.contentLayer.batchDraw(); } }

  // Fit framing the main cluster — a stray element far from the rest no longer
  // blows up the view (it's excluded), and the scale is clamped to a sane range.
  fitView(state) {
    const visible = visibleElements(state);
    const strays = new Set(strayIds(visible));
    const elements = visible.filter((element) => !strays.has(element.id));
    return this.boundsView(contentBounds(elements.length ? elements : visible), 0.02, 4);
  }

  // Fit framing EVERYTHING (including strays) — used by "Show" on the warning.
  fitAllView(state) {
    return this.boundsView(contentBounds(visibleElements(state)), 0.005, 4);
  }

  boundsView(b, minScale, maxScale) {
    const w = this.stage.width(); const h = this.stage.height();
    if (!b) return { scale: 1, posX: w / 2, posY: h / 2 };
    let scale = Math.min(w / (b.w || 1), h / (b.h || 1)) * 0.9 || 1;
    scale = Math.max(minScale, Math.min(maxScale, scale));
    return { scale, posX: w / 2 - b.cx * scale, posY: h / 2 - b.cy * scale };
  }

  // Recentre at 100% zoom, without changing how big anything looks. Centring on
  // the bounds midpoint alone can land in a gap on a spread-out plan, so aim at
  // the element nearest that midpoint — the jump then always hits something.
  centerView(state) {
    const w = this.stage.width(); const h = this.stage.height();
    const visible = visibleElements(state);
    const strays = new Set(strayIds(visible));
    const elements = visible.filter((element) => !strays.has(element.id));
    const pool = elements.length ? elements : visible;
    const b = contentBounds(pool);
    if (!b) return { scale: 1, posX: w / 2, posY: h / 2 };
    let target = { x: b.cx, y: b.cy }; let best = Infinity;
    for (const el of pool) {
      const eb = elementBounds(el);
      if (!eb) continue;
      const cx = eb.x + eb.w / 2; const cy = eb.y + eb.h / 2;
      const d = Math.hypot(cx - b.cx, cy - b.cy);
      if (d < best) { best = d; target = { x: cx, y: cy }; }
    }
    return { scale: 1, posX: w / 2 - target.x, posY: h / 2 - target.y };
  }

  fitToContent() { this.store.set({ view: this.fitView(this.store.state) }, { source: 'fit' }); }
  fitAll() { this.store.set({ view: this.fitAllView(this.store.state) }, { source: 'fit' }); }
  centerOnContent() { this.store.set({ view: this.centerView(this.store.state) }, { source: 'fit' }); }

  // Elements whose center sits far outside the visible cluster.
  strayElementIds(state) { return strayIds(visibleElements(state)); }

  destroy() {
    this._unsub?.();
    window.removeEventListener('resize', this._onResize);
    this._ro?.disconnect();
    this.stage.destroy();
  }
}

// --- helpers ---------------------------------------------------------------

// Whether the browser's 2D canvas context can measure text. Probed once: the
// result is a property of the environment, not of any one render.
let _canMeasureText;
function canMeasureText() {
  if (_canMeasureText === undefined) {
    try {
      const ctx = document.createElement('canvas').getContext('2d');
      _canMeasureText = !!ctx && typeof ctx.measureText === 'function';
    } catch (_) {
      _canMeasureText = false;
    }
  }
  return _canMeasureText;
}

function labelText(labels = [], species = {}) {
  const lines = [];
  for (const label of labels || []) {
    if (label.type === 'custom') {
      if (label.visible !== false && label.value) lines.push(label.value);
    } else if (label.type === 'species') {
      const s = species[label.id];
      if (!s) continue;
      for (const key of label.keys || []) {
        const v = Array.isArray(s[key]) ? s[key][0] : s[key];
        if (v) lines.push(v);
      }
    }
  }
  return lines.join('\n');
}

function centerOf(el) {
  const d = el.data || {};
  if (el.type === 'ellipse' || el.type === 'rectangle') return { x: d.x, y: d.y };
  const pts = (d.coordinates || []).filter((c) => Array.isArray(c) && typeof c[0] === 'number');
  if (!pts.length) return null;
  return { x: pts.reduce((a, c) => a + c[0], 0) / pts.length, y: pts.reduce((a, c) => a + c[1], 0) / pts.length };
}

function coordPoints(coordinates = []) {
  const pts = [];
  for (const c of coordinates) {
    if (Array.isArray(c) && typeof c[0] === 'number') pts.push(c[0], c[1]);
  }
  return pts;
}

function shiftCoords(coordinates = [], dx, dy) {
  return coordinates.map((c) => {
    if (!Array.isArray(c)) return c;
    const out = c.slice();
    // numeric pairs live at [0,1] and (for curves) at [3,4]/[5,6]
    for (let i = 0; i < out.length - 1; i += 1) {
      if (typeof out[i] === 'number' && typeof out[i + 1] === 'number') { out[i] += dx; out[i + 1] += dy; i += 1; }
    }
    return out;
  });
}

// Bounding box of a single element (null if it has no geometry).
function elementBox(el) {
  const d = el.data || {};
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') {
    const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
    minX = d.x - hw; maxX = d.x + hw; minY = d.y - hh; maxY = d.y + hh;
  } else {
    for (const c of d.coordinates || []) {
      if (Array.isArray(c) && typeof c[0] === 'number') { minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]); minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]); }
    }
  }
  if (!isFinite(minX)) return null;
  return { id: el.id, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}

function quantile(sorted, q) {
  if (!sorted.length) return 0;
  const i = (sorted.length - 1) * q; const lo = Math.floor(i); const hi = Math.ceil(i);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (i - lo);
}

// Ids of elements whose center is a far outlier from the cluster — a single
// mis-placed object hundreds of metres away. Uses an IQR fence on the distance
// to the median center, plus an absolute floor so a normally-spread plan never
// trips it. Returns [] when there are too few elements to judge.
function strayIds(elements) {
  const boxes = elements.map(elementBox).filter(Boolean);
  if (boxes.length < 5) return [];
  const cxs = boxes.map((b) => b.cx).sort((a, b) => a - b);
  const cys = boxes.map((b) => b.cy).sort((a, b) => a - b);
  const medx = quantile(cxs, 0.5); const medy = quantile(cys, 0.5);
  const dists = boxes.map((b) => Math.hypot(b.cx - medx, b.cy - medy));
  const sd = dists.slice().sort((a, b) => a - b);
  const fence = quantile(sd, 0.75) + 3 * (quantile(sd, 0.75) - quantile(sd, 0.25));
  // Core extent from the inliers only (dist within the fence) so the strays
  // themselves don't inflate the floor.
  const inX = []; const inY = [];
  boxes.forEach((b, i) => { if (dists[i] <= fence) { inX.push(b.cx); inY.push(b.cy); } });
  const ext = (arr) => (arr.length ? Math.max(...arr) - Math.min(...arr) : 0);
  const floor = 4 * Math.max(ext(inX), ext(inY), 100); // never flag tight/normal plans
  const out = [];
  boxes.forEach((b, i) => { if (dists[i] > fence && dists[i] > floor) out.push(b.id); });
  return out;
}

// Bounding box of one element in plan coordinates (null if it has no geometry).
export function elementBounds(el) {
  const d = el.data || {};
  if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') {
    const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
    if (!isFinite(d.x) || !isFinite(d.y)) return null;
    return { x: d.x - hw, y: d.y - hh, w: hw * 2, h: hh * 2 };
  }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of d.coordinates || []) {
    if (Array.isArray(c) && typeof c[0] === 'number') { minX = Math.min(minX, c[0]); maxX = Math.max(maxX, c[0]); minY = Math.min(minY, c[1]); maxY = Math.max(maxY, c[1]); }
  }
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// True when the plan has geometry but the current viewport doesn't overlap it —
// the user has panned/zoomed completely away from their content.
export function contentOffscreen(state, width, height, margin = 8) {
  const v = state.view || {};
  const scale = v.scale || 1;
  const x0 = -(v.posX || 0) / scale; const y0 = -(v.posY || 0) / scale;
  const vx1 = x0 + (width || 0) / scale; const vy1 = y0 + (height || 0) / scale;
  if (!(vx1 > x0) || !(vy1 > y0)) return false;
  const m = margin / scale; // screen-px margin in plan units
  const elements = visibleElements(state);
  for (const element of elements) {
    const bounds = elementBounds(element);
    if (bounds && !(bounds.x > vx1 + m || bounds.x + bounds.w < x0 - m || bounds.y > vy1 + m || bounds.y + bounds.h < y0 - m)) return false;
  }
  return elements.length > 0;
}

function visibleElements(state) {
  const layers = new Map((state.layers || []).map((layer) => [layer.id, layer]));
  return (state.elements || []).filter((element) => {
    const data = element.data || {};
    const layer = data.layer ? layers.get(data.layer) : null;
    return !(element.type === 'image' && data.visible === false) &&
      !(layer && layer.visible === false) &&
      !(state.timelineOn && !visibleAtMonth(element, state.timelineMonth));
  });
}

function contentBounds(elements) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const acc = (x, y) => { minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y); };
  for (const el of elements) {
    const d = el.data || {};
    if (el.type === 'ellipse' || el.type === 'rectangle' || el.type === 'image') {
      const hw = (d.width || 0) / 2; const hh = (d.height || d.width || 0) / 2;
      acc(d.x - hw, d.y - hh); acc(d.x + hw, d.y + hh);
    } else {
      for (const c of d.coordinates || []) if (Array.isArray(c) && typeof c[0] === 'number') acc(c[0], c[1]);
    }
  }
  if (!isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY, cx: (minX + maxX) / 2, cy: (minY + maxY) / 2 };
}
